/**
 * The ONE consistent frame every surface renders inside (PC-F06): the same header, the same
 * navigation, the same search field and the same footer, in the same place, on every page.
 * No surface looks like a different product.
 *
 *  PC-F06   One frame — header, nav, search, footer — shared by every page.
 *  PC-F06a  A search field at the TOP of every page, same position, visible without
 *           scrolling, and never reduced to an icon alone. It may be non-functional in
 *           Stage 1, but it must be present and labelled.
 *  PC-F10   The primary navigation is exactly the seven entries in PRIMARY_NAV, in order.
 *  PC-F14   No named future capability anywhere — no "coming soon", no disabled feature.
 *  PC-F23   Synthetic-data build: the reader must be told the figures are synthetic. The
 *           standing amber banner that used to sit above the chrome has been REMOVED at the
 *           user's request; the claim now rides on the footer line ("Synthetic demo data
 *           throughout"), which is present on every page for the same reason the banner was.
 *           The `--tb-banner-*` tokens survive in frame.css — six other files use that amber
 *           as their generic note/warning palette, so they are no longer banner-specific.
 *  PC-NFR01 WCAG 2.1 AA — labelled controls, a #main skip-link target, sufficient contrast.
 *
 * CSP (server.ts helmet) forbids any external font, stylesheet or script, so all styling is
 * a single inline <style> block over a system font stack, and the frame makes no network
 * requests of any kind. The one <style> is scoped by element/class names local to the frame.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';

/**
 * The generated per-tool preview art for the search dropdown's tool rows.
 *
 * STATIC, and deliberately so after checking. `lazy` looked obviously right here — App.tsx is the frame
 * every route renders inside, so it IS the main chunk, and the mark is only ever seen once the search
 * field is focused. But `design/FlowArt` is already statically imported by the upload wizard's
 * `PreviewCard`/`ReviewPreviewCard`, and the wizard is itself a static import at
 * `tools/ShareOverlay.tsx`, so the module is in this chunk either way. Rollup confirms it: a `lazy` here
 * earns the "dynamic import will not move module into another chunk" warning and adds a <Suspense> that
 * can never suspend. See the matching note in Profile.tsx for what would have to change first.
 */
import { FlowArt } from './design/FlowArt';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import { pageTransition } from './tools/upload/design/motion';
import {
  // PRIMARY_NAV is no longer rendered as a bar — the Menu Bar redesign moved those destinations
  // into the profile dropdown and onto the Home page (see MenuBar.tsx / the <MenuBar> in App()).
  // ROLE_GATED_NAV stays: roleGatedEntriesFor() still reads it to gate the dropdown's Management
  // dashboard row.
  ROLE_GATED_NAV,
  isSearchable,
  MIN_QUERY_LENGTH,
  type AppointedRole,
  type PersonSuggestion,
  type ToolSuggestion,
} from '@trailblazer/shared';
import { fetchSuggestions } from './search/api';
import { getHome, type HomeToolCard } from './home/api';
import { SearchIcon } from './tools/upload/design/icons';
import { monogram } from './Profile';
import { NotificationBell } from './notifications/NotificationBell';
import { setSkin, useSkin, type Skin } from './tools/upload/skinStore';
import { useRecentTools } from './tools/recentlyClickedStore';
// VwRoundel is no longer imported here — the Menu Bar owns the brand mark now (MenuBar.tsx).
// (Staging re-added a `VwRoundel` import alongside the Terms gate below; dropped in the merge
// because nothing in this branch's body renders it — MenuBar draws the roundel itself.)
import {
  MenuBar,
  ResponsiveSearch,
  DropdownCard,
  DropdownRow,
  DropdownDivider,
} from './tools/upload/design/MenuBar';
import {
  ProfileGlyph,
  BookmarkGlyph,
  GridGlyph,
  ForumGlyph,
} from './tools/upload/design/menuIcons';
import { TermsGate, TermsFooterLink } from './terms/TermsGate';
import { postSignOutDestination } from './kombi/adapters/liveSession';
import { FloatingShareAction } from './kombi/FloatingShareAction';
import { kombiProductionRoutes } from './kombi/productionRoutes';

// NOTE (design-system merge): staging kept the frame's CSS as an inline `FRAME_STYLES` template
// literal rendered from a <style> tag in the frame. On THIS branch that CSS was moved out to
// `src/frame.css` (imported once in main.tsx) so the reset does not come and go with a modal — see
// frame.css's header and the note at the <main> below. Staging's inline string is therefore not
// carried here; frame.css is its home now.

/**
 * The header sign-in/out affordance's state, derived from a single probe of OUR backend.
 *
 * On mount we ask `GET /api/me` once (same-origin, sending the app session cookie). A 200 means
 * signed in — we keep the display name for the greeting. A 401 is the normal signed-out answer.
 * Anything else (or a network fault) we treat as "unknown" and simply render nothing, so a
 * backend blip never breaks the frame. The browser talks only to our gateway here — no token,
 * no Microsoft host, no client secret ever touches this code.
 *
 * This is a lightweight probe, independent of the /me route loader: the affordance lives in the
 * frame and must reflect sign-in state on every page, not just /me.
 */
/**
 * The caller's Terms-of-Use gate state (TU-F06), carried on the signed-in probe. `acceptedCurrent`
 * false means the frame renders the acceptance gate in place of the app. `currentVersionId` is the
 * version a subsequent accept must echo. See db/termsOfUse.ts for the server side.
 */
export interface TermsGateState {
  acceptedCurrent: boolean;
  currentVersionId: string | null;
  currentVersionLabel: string | null;
}

type AuthState =
  | { status: 'loading' }
  | {
      status: 'signed-in';
      displayName: string;
      roles: AppointedRole[];
      termsOfUse: TermsGateState;
    }
  | { status: 'signed-out' };

/**
 * The frame's sign-in probe. Returns the auth state plus a `reload` that re-runs the probe WITHOUT a
 * full-page navigation — the Terms gate calls it after a successful accept so the app appears in place
 * (the SPA state and scroll position survive), rather than reloading the world.
 */
function useAuth(): { auth: AuthState; reload: () => void } {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  // A bump counter the effect depends on; incrementing it re-runs the probe.
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/me', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 401) {
          setState({ status: 'signed-out' });
          return;
        }
        if (!res.ok) {
          // A genuine fault (5xx, etc.): don't assert either state — render nothing.
          setState({ status: 'loading' });
          return;
        }
        const me = (await res.json()) as {
          display_name?: string;
          roles?: AppointedRole[];
          termsOfUse?: Partial<TermsGateState>;
        };
        if (cancelled) return;
        setState({
          status: 'signed-in',
          displayName: me.display_name ?? 'your account',
          roles: me.roles ?? [],
          // Default to "already accepted" if the field is somehow absent, so a probe-shape gap can
          // never trap a signed-in person behind a gate with no way through (mirrors TU-NFR07).
          termsOfUse: {
            acceptedCurrent: me.termsOfUse?.acceptedCurrent ?? true,
            currentVersionId: me.termsOfUse?.currentVersionId ?? null,
            currentVersionLabel: me.termsOfUse?.currentVersionLabel ?? null,
          },
        });
      })
      .catch(() => {
        // Network error: leave the affordance absent rather than guessing.
        if (!cancelled) setState({ status: 'loading' });
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { auth: state, reload: () => setNonce((n) => n + 1) };
}

/** The role-gated nav entries the signed-in caller's live roles unlock (any-of match). */
function roleGatedEntriesFor(roles: readonly AppointedRole[]) {
  return ROLE_GATED_NAV.filter((entry) => entry.requires.some((r) => roles.includes(r)));
}

/**
 * Sign out by POSTing to the backend (POST, not a link, so a stray GET/prefetch can never end a
 * session), then reload so every surface re-probes and drops to the signed-out view. The backend
 * clears the session cookie; the browser never touches Microsoft. The landing URL carries the
 * `sso=none` loop breaker so home's loader shows the signed-out view instead of immediately
 * re-attempting silent SSO.
 */
function signOut(): void {
  void fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' }).finally(() => {
    window.location.assign(postSignOutDestination('/'));
  });
}

/*
 * The old top-right AuthControl (a `.tb-auth` name + Sign out / Sign in link) is GONE: the Menu Bar
 * redesign folds its signed-in branch into ProfileMenu (Sign out lives at the foot of the account
 * dropdown now) and renders its signed-out branch (the Sign in link) as the Menu Bar's account
 * slot directly in App(). See the account slot in App()'s <MenuBar> below.
 */

/**
 * ProfileMenu — a profile-monogram dropdown in the header, carrying the per-browser APP SKIN toggle.
 * Modeled on NotificationBell's dropdown pattern (useState(open) + click-outside mousedown +
 * aria-haspopup/aria-expanded + a `role="menu"` panel), and reuses the `.tb-notif*` panel classes so
 * no new dropdown chrome is invented — only the trigger is `.tb-profile*`.
 *
 * THREE items, each a `role="menuitemradio"` reflecting the skin (`aria-checked`): V1 (the original
 * CSS/React, default), V2 (the design system with hand-composed pages) and V3 (the same design system
 * with the designer's own page files). It was one `menuitemcheckbox` while there were two skins; three
 * mutually exclusive values need radios, since an on/off item cannot say which of three is chosen.
 *
 * The hint says plainly that V2/V3 restyle the WHOLE app and that V1 is kept as a reference — the
 * design covers the upload flow only, so every other route still renders V1's markup under the global
 * reset and therefore looks unstyled. That is the intended signal: it shows which sections the design
 * has yet to cover.
 */
/**
 * The three selectable skins, in the order they are offered. V2 and V3 render the SAME design system;
 * they differ only in who composed the pages — V2 by hand, V3 from the designer's own page files copied
 * verbatim. Both are kept so they can be compared directly. See skinStore.ts.
 */
const SKIN_CHOICES: { value: Skin; label: string; hint: string }[] = [
  {
    value: 'v1',
    label: 'Version 1 (original)',
    hint: 'Version 1: the original look, kept for reference. Saved for this browser only.',
  },
  {
    value: 'v2',
    label: 'Version 2 (design system)',
    hint: 'Version 2 restyles the whole app, with the upload pages composed by hand. Only the upload flow is designed so far — other pages show Version 1 content unstyled.',
  },
  {
    value: 'v3',
    label: 'Version 3 (design files)',
    hint: 'Version 3 is the same design system, but the upload pages are the design files themselves, wired to live data. The wizard opens on the Automation picker, as the design draws it.',
  },
];

/**
 * ProfileMenu — the Menu Bar's account dropdown, built to the designer's "Menu bar Dropdown"
 * frame (node 7:408). The monogram trigger opens a card carrying every destination the trimmed
 * top-row nav no longer shows, so nothing became unreachable in the redesign (see MenuBar's
 * header on the PC-F10 deviation). Order, top → bottom, is the designer's:
 *
 *   View Kombi Profile → /me
 *   Saved Tools        → (no route yet — deliberately inert, per the product owner)
 *   ───────────────────
 *   My Learning        → /learning
 *   Kombi Forum        → /community
 *   ───────────────────
 *   Management dashboard → /dashboard   (role-gated: Manager only — the product owner placed it
 *                                        here, "below the line below Kombi Forum, above the
 *                                        version picker")
 *   Design Version History → the V1/V2/V3 skin switcher (was the old "Appearance" menu)
 *   Sign out           → ends the session   (added below the picker — the trimmed nav dropped
 *                                            the old AuthControl's Sign out, so it lives here now)
 *
 * The skin switcher keeps its full a11y semantics from the previous ProfileMenu: three
 * `menuitemradio` rows reporting `aria-checked` against the live skin, since the choice is
 * mutually exclusive between three values.
 */
function ProfileMenu({
  displayName,
  roles,
}: {
  displayName: string;
  roles: readonly AppointedRole[];
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const skin = useSkin();
  // The Management dashboard row appears only when the caller actually holds a role that unlocks
  // it — the same any-of gate the old nav used (roleGatedEntriesFor), so a non-Manager never sees
  // a dead-end link. Manager is the only role on that entry today.
  const showDashboard = roleGatedEntriesFor(roles).some((e) => e.path === '/dashboard');

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Account trigger — the caller's NAME as text (product owner's request), with the
          monogram roundel removed entirely. The name is the whole trigger; the aria-label
          keeps the "account and preferences" purpose for assistive tech. */}
      <button
        type="button"
        className="flex items-center rounded-md px-4 py-2 text-sm font-light text-ink transition-colors hover:bg-accent-subtle focus-visible:border focus-visible:border-ring-focus focus-visible:outline-none"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${displayName} — account and preferences`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate">{displayName}</span>
      </button>

      {open ? (
        <DropdownCard>
          <DropdownRow
            icon={<ProfileGlyph />}
            label="View Kombi Profile"
            to="/me"
            onClick={() => setOpen(false)}
          />
          {/* Saved Tools has no destination yet — the product owner asked to leave it unlinked.
              Rendered as an inert row (no `to`, no `onClick`) so it reads as present-but-quiet
              rather than a broken link. */}
          <DropdownRow icon={<BookmarkGlyph />} label="Saved Tools" />

          <DropdownDivider />

          <DropdownRow
            icon={<GridGlyph />}
            label="My Learning"
            to="/learning"
            onClick={() => setOpen(false)}
          />
          <DropdownRow
            icon={<ForumGlyph />}
            label="Kombi Forum"
            to="/community"
            onClick={() => setOpen(false)}
          />

          <DropdownDivider />

          {showDashboard ? (
            <DropdownRow
              icon={<GridGlyph />}
              label="Management dashboard"
              to="/dashboard"
              onClick={() => setOpen(false)}
            />
          ) : null}

          {/* Design Version History — the skin switcher, kept as a labelled radiogroup so the
              three mutually-exclusive versions read correctly to assistive tech. */}
          <p className="px-5 pb-1 pt-2 text-[10px] font-light uppercase tracking-wide text-icon">
            Design Version History
          </p>
          <div role="group" aria-label="Design Version History">
            {SKIN_CHOICES.map((choice) => (
              <DropdownRow
                key={choice.value}
                role="menuitemradio"
                ariaChecked={skin === choice.value}
                label={`${skin === choice.value ? '✓ ' : ''}${choice.label}`}
                onClick={() => setSkin(choice.value)}
              />
            ))}
          </div>

          <DropdownDivider />

          {/* Sign out — the redesign folded AuthControl's Sign out into this menu (the trimmed
              chrome no longer carries it). Still a POST via signOut(), not a link. */}
          <DropdownRow label="Sign out" onClick={signOut} />
        </DropdownCard>
      ) : null}
    </div>
  );
}

/**
 * The ONE search field, hosted by the frame at the top of every page (`PC-F06a`, `DS-F16`) and
 * behaving as `DS-F17`–`DS-F20` require.
 *
 * WHAT IT SEARCHES: the tool catalogue AND the people directory (`DS-F17`, `DS-F21`) — two kinds of
 * result, "kept visibly separate and never interleaved into one ranked list". People are real
 * colleagues who have signed in: their name and Graph-synced job title and department, plus the skills
 * they declared themselves (`UR-F31`). Nothing is invented and no directory is enumerated — a person
 * this platform has never seen is not searchable, which is the honest behaviour.
 *
 * PEOPLE ARE NEVER RANKED OR SCORED (`PC-F31`, `DS-NFR04`): person results carry no figures, no
 * ordering by anything a person has done, published or been given. They are ordered by closeness of
 * match to the query alone, and otherwise alphabetically. This is a hard rule, not a default.
 *
 * The label names BOTH kinds because both now work. `PC-F14` forbids naming a capability that does not
 * exist, so these strings were "Search tools" alone until people search shipped — they changed in the
 * same commit that implemented it, which is the rule this comment used to describe.
 *
 * SUBMITTING (`DS-F18`) navigates to the full results view on the Tools page carrying the query, so
 * filters and sorts live there and only there (`DS-F19`). An empty query does nothing — not a
 * navigation to an empty search, literally nothing, which is what the requirement says.
 *
 * ON THE TOOLS PAGE THIS FIELD *IS* THE PAGE'S SEARCH FIELD (`DS-F18`: "there are never two search
 * boxes on one page"). That is why it reads its value back from `?q=` while on /tools: the Tools page
 * no longer renders a search input of its own, so this one must show what was searched for, stay
 * populated through a filter change, and follow back/forward.
 *
 * NOT RECORDED (`AE-F36`, `DS-F19`): nothing here stores a history of what was typed. There is
 * deliberately no "recent searches" list — the requirements rule it out precisely because it would
 * mean keeping a record of what a person searched for.
 *
 * ACCESSIBILITY (`DS-NFR07`): a VISIBLE text label rather than a placeholder alone, the ARIA combobox
 * pattern over a listbox, full keyboard operation, and the number of suggestions announced politely.
 * The previous field hid its label with clip-rect; DS-NFR07 asks for a visible one, so it now shows.
 *
 * NEVER BLOCKS THE PAGE (`DS-NFR08`): suggestions are debounced, awaited by nothing the page needs,
 * and every failure mode collapses to an empty dropdown.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const listboxId = useId();
  const inputId = useId();
  const statusId = useId();

  // On the Tools page this field IS the page's search box, so it mirrors the URL. Anywhere else it
  // starts empty — a query typed on the Community page should not persist onto an unrelated surface.
  const onToolsPage = location.pathname === '/tools';
  const urlQuery = onToolsPage ? (params.get('q') ?? '') : '';

  const [value, setValue] = useState(urlQuery);
  const [tools, setTools] = useState<ToolSuggestion[]>([]);
  const [people, setPeople] = useState<PersonSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  /** Which suggestion the keyboard is on; -1 means "none — Enter submits the query instead". */
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef<number | undefined>(undefined);

  // The EMPTY-STATE sources (shown when the field is open but not yet a searchable query):
  // "Recently clicked" = this browser's tool-view history (recentlyClickedStore), and "Popular"
  // = the homepage's trending/all-time boards (/api/home). Neither is search history (AE-F36/
  // DS-F19): one is pages the user opened, the other is a platform ranking. No PEOPLE appear in
  // the empty state — people only surface once the user types (DS-F21 suggestions).
  const recentTools = useRecentTools();
  const [popular, setPopular] = useState<HomeToolCard[]>([]);
  const popularLoaded = useRef(false);

  // Follow the URL. Covers arriving at /tools from a submission, a filter change re-running the
  // loader, back/forward, and leaving /tools (which clears the field).
  useEffect(() => {
    setValue(urlQuery);
    setOpen(false);
    setActiveIndex(-1);
  }, [urlQuery]);

  // Popular tools, fetched lazily the first time the field is opened (not on mount — the search box
  // is on every page, and most page loads never open it). Trending first, then best-of-all-time,
  // de-duped by toolId and capped short. Graceful-degrade to none on any non-ok result — the empty
  // state simply drops the "Popular" section, matching /api/home's own EMPTY_HOME_DATA posture.
  useEffect(() => {
    if (!open || popularLoaded.current) return;
    popularLoaded.current = true;
    void getHome().then((res) => {
      if (res.kind !== 'ok') return;
      const seen = new Set<string>();
      const merged: HomeToolCard[] = [];
      for (const t of [...res.body.trending, ...res.body.bestOfAllTime]) {
        if (seen.has(t.toolId)) continue;
        seen.add(t.toolId);
        merged.push(t);
        if (merged.length >= 4) break;
      }
      setPopular(merged);
    });
  }, [open]);

  // ── suggestions (DS-F20, DS-F21) ──
  // Debounced so a fast typist causes one request rather than one per character, and cancelled on
  // change so a slow earlier response can never overwrite a newer one with stale names.
  useEffect(() => {
    if (!isSearchable(value)) {
      setTools([]);
      setPeople([]);
      setActiveIndex(-1);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchSuggestions(value).then((found) => {
        if (cancelled) return;
        setTools(found.tools);
        setPeople(found.people);
        setActiveIndex(-1);
      });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value]);

  /**
   * The two groups flattened into ONE keyboard sequence.
   *
   * DS-F17 requires tools and people to be "kept visibly separate and never interleaved into one ranked
   * list" — that is about DISPLAY, and the two groups below render separately with their own headings.
   * Keyboard traversal is a different concern: a person pressing ArrowDown expects to keep moving
   * through everything on offer, not to stop dead at a group boundary. So the visual grouping is
   * preserved while `activeIndex` walks a single flat sequence.
   *
   * Tools first because the query was typed into a field on a tool platform; within each group the
   * server's order is preserved untouched (for people that is match tier then alphabetical — never a
   * ranking of the people themselves, per PC-F31).
   */
  const flat: (
    { kind: 'tool'; tool: ToolSuggestion } | { kind: 'person'; person: PersonSuggestion }
  )[] = [
    ...tools.map((tool) => ({ kind: 'tool' as const, tool })),
    ...people.map((person) => ({ kind: 'person' as const, person })),
  ];

  // The dropdown now has TWO states. When the query is searchable it shows live suggestions
  // (`flat`, the typing state); otherwise it shows the empty state (recently-clicked + popular).
  // `showList` keeps its old meaning — "the typing-state listbox is up" — because the keyboard
  // handler and the combobox aria wiring below key off it. `showEmptyState` is the new focused-
  // but-not-typing panel; `showDropdown` is either.
  const searchable = isSearchable(value);
  const showList = open && searchable && flat.length > 0;
  const showEmptyState = open && !searchable && (recentTools.length > 0 || popular.length > 0);
  const showDropdown = showList || showEmptyState;

  function submitQuery(raw: string) {
    const term = raw.trim();
    if (!isSearchable(term)) return; // DS-F18: "An empty query does nothing."
    setOpen(false);
    setActiveIndex(-1);
    // DS-F18: the full results view on the Tools page, carrying the query. Filters and sorts are
    // deliberately NOT set here (DS-F19) — the Tools page owns those, and a fresh search should land
    // on the first page rather than inherit an old offset.
    navigate(`/tools?q=${encodeURIComponent(term)}`);
  }

  /** Open whichever kind of suggestion this is: a tool's detail page, or a person's profile. */
  function openSuggestion(item: (typeof flat)[number]) {
    setOpen(false);
    setActiveIndex(-1);
    if (item.kind === 'tool') {
      // DS-F20: a tool suggestion opens "that tool's detail page directly".
      navigate(`/tools/${item.tool.toolId}`);
    } else {
      // DS-F21: "a person suggestion opens that person's profile". We open the single
      // universal public profile at /users/:userId (readPublicProfile) rather than the
      // thinner /people/:userId view. REQUIREMENTS_PROFILE.md §3.2 defines ONE public
      // visibility model for every authenticated employee — and it INCLUDES the follower
      // count (PR-F02, PR-F52). The /people page under-rendered that model (no count);
      // /users is the faithful §3.2 view. PC-F31 only forbids RANKING people, not a bare
      // count — /users shows no position or ordering (PR-NFR05), so this stays compliant.
      navigate(`/users/${item.person.userId}`);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      // Close the list but keep the text: the person is dismissing suggestions, not their query.
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!showList) {
      // ArrowDown with a closed list but loaded suggestions re-opens it, as people expect.
      if (e.key === 'ArrowDown' && flat.length > 0) {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Wraps across the whole flattened sequence, so ArrowDown crosses from the last tool into the
      // first person without the person having to know a boundary exists.
      setActiveIndex((i) => (i + 1) % flat.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flat.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      // A highlighted suggestion wins over submitting the raw query; with none highlighted the form's
      // own submit runs instead, which is DS-F20's "can always ignore them and submit the query".
      e.preventDefault();
      const chosen = flat[activeIndex];
      if (chosen) openSuggestion(chosen);
    }
  }

  /**
   * DS-F20 requires suggestions to obey the health rules "exactly as full results do", and DS-F13 asks
   * for a Broken tool to be labelled where explicitly searched. So the dropdown labels the two states
   * that change what the tool is worth to the reader, and says nothing for a healthy one.
   */
  function healthNote(s: ToolSuggestion): string | null {
    if (s.healthState === 'Broken') return 'Broken';
    if (s.healthState === 'Unmaintained') return 'Unmaintained';
    return null;
  }

  return (
    // `tb-search` is kept ONLY for `position:relative` (frame.css), which anchors the
    // dropdown below the bar. The BAR itself is the design's Menu Bar search bar: the
    // accent-subtle pill from Search Bar.svg (SearchInput's geometry), 194px wide at rest,
    // growing to fill when the icon expands it. The dropdown below now wears the design
    // system's DropdownCard skin (matching the profile and notification menus), replacing
    // the old V1 `.tb-search-list` chrome — see the Figma "Frame51"/"SearchStuff" nodes.
    <div className="tb-search w-[194px] max-w-full">
      <form
        role="search"
        aria-label="Search tools and people"
        // The frame's search bar: h-pill accent-subtle rounded-md, 12px gap, teal search
        // glyph, 13px Inter Light. Matches Search Bar.svg's "Bar" cell exactly (see
        // SearchInput.tsx for the shared geometry this mirrors).
        //
        // The border is ALWAYS present (transparent) and only its COLOUR changes on focus —
        // NOT `focus-within:border`, which would add a 1px border on focus and, since the
        // border eats into the border-box, visibly nudge the icon/input inward. Reserving
        // the 1px up front means focus paints the outline in place with no layout shift.
        className="flex h-pill w-full items-center gap-5 rounded-md border border-transparent bg-accent-subtle px-pill-x text-sm font-light text-text focus-within:border-ring-focus"
        onSubmit={(e) => {
          e.preventDefault();
          submitQuery(value);
        }}
      >
        <SearchIcon className="shrink-0 text-icon" />
        {/* DS-NFR07: a VISIBLE label — kept for assistive tech, hidden from sight the same
            way the status line is (the frame draws only the placeholder "Search"). */}
        <label htmlFor={inputId} className="sr-only">
          Search tools and people
        </label>
        <input
          id={inputId}
          type="search"
          name="q"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Deferred: clicking a suggestion blurs the input first, and closing immediately would
            // unmount the option before its click landed.
            blurTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          placeholder="Search"
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-describedby={statusId}
          // Transparent input filling the bar — inherits the bar's size/weight/colour so it
          // reads exactly like the design's static "Search" label. `appearance-none` strips
          // the browser's native `type=search` chrome: WebKit otherwise draws its OWN rounded
          // inner field (an 8px pill inside the bar) that the design has no place for — the bar
          // is the only surface, the input is just text on it.
          className="w-full min-w-0 appearance-none rounded-none bg-transparent text-inherit placeholder:text-text focus:outline-none"
          {...(activeIndex >= 0 ? { 'aria-activedescendant': `${listboxId}-${activeIndex}` } : {})}
        />
      </form>

      {/*
        DS-NFR07: "the number of suggestions announced", and "where suggestions cover both tools and
        people, each group is announced by name". So the count names both groups rather than giving one
        total — a screen-reader user needs to know there are people below the tools, not just that there
        are eight of something. The empty state announces its own two sections likewise.
      */}
      <p id={statusId} role="status" aria-live="polite" className="tb-search-status">
        {searchable
          ? showList
            ? `${tools.length} tool${tools.length === 1 ? '' : 's'}, ` +
              `${people.length} ${people.length === 1 ? 'person' : 'people'}`
            : 'No suggestions'
          : showEmptyState
            ? [
                recentTools.length > 0 ? 'recently clicked tools' : '',
                popular.length > 0 ? 'popular tools' : '',
              ]
                .filter(Boolean)
                .join(', ')
            : `Type at least ${MIN_QUERY_LENGTH} characters`}
      </p>

      {showList ? (
        // TYPING STATE — the live suggestions. The visual shell matches DropdownCard (white card,
        // r8, teal shadow, 320px) but the ROLE is listbox, not menu: the input is a combobox and
        // these are its options, wired for `aria-activedescendant`. `activeIndex` indexes the
        // FLATTENED sequence (tools then people), so ArrowDown crosses the group boundary without
        // stopping — the two groups stay visually separate (DS-F17) with their own visible headings
        // (DS-NFR07), only the keyboard path is unified.
        <div
          className={SEARCH_CARD_CLS}
          id={listboxId}
          role="listbox"
          aria-label="Search suggestions"
        >
          {tools.length > 0 ? (
            <div role="group" aria-labelledby={`${listboxId}-tools-h`}>
              <SearchGroupHeading id={`${listboxId}-tools-h`}>Tools</SearchGroupHeading>
              {tools.map((s, i) => (
                <ToolResultRow
                  key={s.toolId}
                  id={`${listboxId}-${i}`}
                  name={s.name}
                  // Publisher ("by …") is the row's subtitle, matching the Popular cards — EXCEPT a
                  // Broken/Unmaintained/Unverified tool keeps its health/trust label, which DS-F20
                  // requires the dropdown to show exactly as full results do. So the label wins when
                  // present, and a healthy/verified tool shows its publisher instead.
                  note={
                    healthNote(s) ??
                    (s.trustLabel === 'Unverified'
                      ? 'Unverified'
                      : s.ownerName
                        ? `by ${s.ownerName}`
                        : null)
                  }
                  active={i === activeIndex}
                  onMouseEnter={() => setActiveIndex(i)}
                  onChoose={() => {
                    window.clearTimeout(blurTimer.current);
                    openSuggestion({ kind: 'tool', tool: s });
                  }}
                />
              ))}
            </div>
          ) : null}

          {people.length > 0 ? (
            <div role="group" aria-labelledby={`${listboxId}-people-h`}>
              <SearchGroupHeading id={`${listboxId}-people-h`}>Profiles</SearchGroupHeading>
              {people.map((p, n) => {
                const i = tools.length + n; // offset into the flattened keyboard sequence
                return (
                  <PersonResultRow
                    key={p.userId}
                    id={`${listboxId}-${i}`}
                    person={p}
                    active={i === activeIndex}
                    onMouseEnter={() => setActiveIndex(i)}
                    onChoose={() => {
                      window.clearTimeout(blurTimer.current);
                      openSuggestion({ kind: 'person', person: p });
                    }}
                  />
                );
              })}
            </div>
          ) : null}

          {/* "See all results" — the frame's teal footer. Runs the same submit as pressing Enter
              with nothing highlighted (DS-F18): the full results view on /tools carrying the query. */}
          <DropdownDivider />
          <button
            type="button"
            className="w-full rounded-md px-5 py-2 text-center text-xs font-light text-accent transition-colors hover:bg-[#e5f4f4] focus-visible:bg-[#e5f4f4] focus-visible:outline-none"
            onMouseDown={(e) => {
              e.preventDefault();
              window.clearTimeout(blurTimer.current);
              submitQuery(value);
            }}
          >
            See all results
          </button>
        </div>
      ) : showEmptyState ? (
        // EMPTY STATE — focused, nothing typed yet. Two tool sections: "Recently clicked" (this
        // browser's tool-view history) above "Popular" (the homepage boards). NO people here —
        // people only appear once the user types (a suggestion, DS-F21). Each section hides itself
        // when empty, so a fresh visitor with no history sees only "Popular". This is a real menu
        // of navigations (each row opens a tool), so DropdownCard's menu semantics fit.
        <DropdownCard className={SEARCH_CARD_OVERRIDE}>
          {recentTools.length > 0 ? (
            <div role="group" aria-labelledby={`${listboxId}-recent-h`}>
              <SearchGroupHeading id={`${listboxId}-recent-h`}>Recently clicked</SearchGroupHeading>
              {recentTools.slice(0, 4).map((t) => (
                <ToolResultRow
                  key={t.toolId}
                  name={t.name}
                  note={null}
                  onChoose={() => {
                    window.clearTimeout(blurTimer.current);
                    setOpen(false);
                    navigate(`/tools/${t.toolId}`);
                  }}
                />
              ))}
            </div>
          ) : null}

          {popular.length > 0 ? (
            <div role="group" aria-labelledby={`${listboxId}-popular-h`}>
              <SearchGroupHeading id={`${listboxId}-popular-h`}>Popular</SearchGroupHeading>
              {popular.map((t) => (
                <ToolResultRow
                  key={t.toolId}
                  name={t.name}
                  note={t.ownerName ? `by ${t.ownerName}` : null}
                  onChoose={() => {
                    window.clearTimeout(blurTimer.current);
                    setOpen(false);
                    navigate(`/tools/${t.toolId}`);
                  }}
                />
              ))}
            </div>
          ) : null}
        </DropdownCard>
      ) : null}
    </div>
  );
}

/**
 * The search dropdown's shared card skin. The typing-state listbox can't use <DropdownCard> (that
 * component hardcodes role="menu"; the listbox needs its own role + option children), so its shell
 * classes are pulled out here to stay pixel-identical to the card the empty state and the profile/
 * notification menus use. `SEARCH_CARD_OVERRIDE` is the width/scroll override passed to DropdownCard
 * in the empty state; `SEARCH_CARD_CLS` is DropdownCard's own base with the same override appended.
 */
// Centred under the search bar: `left-1/2 -translate-x-1/2` middles the card on the input, and
// `right-auto` neutralises DropdownCard's base `right-0` when this override is appended to it (the
// empty state renders through <DropdownCard>, which hardcodes right-0).
const SEARCH_CARD_OVERRIDE =
  'w-[400px] max-h-[70vh] overflow-y-auto left-1/2 right-auto -translate-x-1/2';
const SEARCH_CARD_CLS =
  'absolute top-full z-20 mt-2 flex flex-col gap-2 rounded-md bg-surface p-4 ' +
  `shadow-[0px_2px_3px_2px_var(--accent-subtle)] ${SEARCH_CARD_OVERRIDE}`;

/**
 * A group heading inside the search dropdown ("Tools" / "Profiles" / "Recently clicked" / "Popular").
 * REAL VISIBLE TEXT, never position-only (DS-NFR07) — the same requirement the old `.tb-search-group`
 * met. Styled as the frame's small muted label; `id` lets a role="group" label itself by it.
 */
function SearchGroupHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} className="px-5 pb-1 pt-2 text-xs font-normal text-icon">
      {children}
    </p>
  );
}

/**
 * A tool row in the search dropdown (Figma "SearchStuff" Tool variant): a thumbnail + name + optional
 * subtitle, on the frame's hover tint. The thumbnail WAS the frame's flat grey placeholder; it is now
 * the tool's own generated mark, drawn from the one field this payload has — its name. ToolSuggestion
 * and HomeToolCard still carry no image (the backend is adding a real image field — see search.ts /
 * home/api.ts), so the swap to a real <img> is still one edit, in one place, right below. `note` is the
 * health/trust/category subtitle (or null to omit the line).
 *
 * `id`/`active` are supplied only in the typing state, where the row is a role="option" of the
 * combobox listbox; in the empty state it is a plain menu button (no id/active). `onChoose` fires on
 * onMouseDown so it lands before the input's blur closes the dropdown (the option is still mounted).
 */
function ToolResultRow({
  id,
  name,
  note,
  active,
  onChoose,
  onMouseEnter,
}: {
  id?: string;
  name: string;
  note: string | null;
  active?: boolean;
  onChoose: () => void;
  onMouseEnter?: () => void;
}) {
  const isOption = id !== undefined;
  return (
    <button
      type="button"
      id={id}
      role={isOption ? 'option' : 'menuitem'}
      {...(isOption ? { 'aria-selected': !!active } : {})}
      className={`flex w-full items-center gap-4 rounded-md px-5 py-3 text-left transition-colors focus-visible:outline-none ${
        active ? 'bg-[#e5f4f4]' : 'hover:bg-[#e5f4f4] focus-visible:bg-[#e5f4f4]'
      }`}
      onMouseDown={(e) => {
        e.preventDefault();
        onChoose();
      }}
      onMouseEnter={onMouseEnter}
    >
      {/* The tool's GENERATED mark, in the frame's own 64x46 box. It replaces a flat #d9d9d9 fill, so
          this row is one hex literal lighter than it was; the box, its radius and its `shrink-0` are
          the frame's measured values and are untouched. `overflow-hidden` is new and only clips the art
          to that radius.

          THE EMBLEM TIER, and permanently so. ToolSuggestion carries no flow structure and must not
          grow one: its own doc comment in packages/api/src/db/tool.ts records that DS-F19 forbids the
          global field growing filters, sorts or settings of its own, and a richer payload is how that
          starts. So the mark is seeded from the NAME — the same seed every other surface uses — which
          is what makes this row and the tool's profile tile show the same mark. No `known`, so the
          connector ticks are drawn in the dashed unknown register rather than as counted pads.

          Still decorative: aria-hidden on the span, no `label` on the art (which makes its own svg
          aria-hidden too), and the name is already in the row text below. */}
      <span aria-hidden="true" className="h-[46px] w-[64px] shrink-0 overflow-hidden rounded-md">
        <FlowArt name={name} width={64} height={46} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-light text-ink">{name}</span>
        {note ? (
          <span className="block truncate text-xs font-light text-[rgb(2_43_48_/_0.6)]">
            {note}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * A person row in the search dropdown (Figma "SearchStuff" Person variant): avatar (or teal monogram)
 * + name + Champion/SWAT markers + "jobTitle · department" subtitle. Only ever rendered in the typing
 * state (people never appear in the empty state), so it is always a role="option". PC-F31: NO figure
 * of any kind appears on a person row — markers are labels, never scores.
 */
function PersonResultRow({
  id,
  person,
  active,
  onChoose,
  onMouseEnter,
}: {
  id: string;
  person: PersonSuggestion;
  active: boolean;
  onChoose: () => void;
  onMouseEnter: () => void;
}) {
  // Job title and department, joined only when both exist — PC-F09 renders without a missing element
  // rather than showing an empty one or a stray separator.
  const sub = [person.jobTitle, person.department].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      className={`flex w-full items-center gap-4 rounded-md px-5 py-3 text-left transition-colors focus-visible:outline-none ${
        active ? 'bg-[#e5f4f4]' : 'hover:bg-[#e5f4f4] focus-visible:bg-[#e5f4f4]'
      }`}
      onMouseDown={(e) => {
        e.preventDefault();
        onChoose();
      }}
      onMouseEnter={onMouseEnter}
    >
      {/* The avatar is decorative: the name is right beside it, so alt text would just repeat it.
          `person.avatar` is null unless it passed the server's MIME allowlist AND the client's
          re-check, so nothing unvalidated reaches src. The monogram fallback is the frame's teal
          circle (accent-subtle fill, --text-on-accent glyph). */}
      {person.avatar ? (
        <img
          className="size-[46px] shrink-0 rounded-full object-cover"
          src={person.avatar}
          alt=""
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex size-[46px] shrink-0 items-center justify-center rounded-full bg-accent-subtle text-sm font-normal text-text-on-accent"
        >
          {monogram(person.name)}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-xs font-light text-ink">{person.name}</span>
          {person.markers.map((m) => (
            <span
              key={m}
              className="shrink-0 rounded-full border border-current px-2 text-[10px] font-normal text-icon"
            >
              {m}
            </span>
          ))}
        </span>
        {sub ? (
          <span className="block truncate text-xs font-light text-[rgb(2_43_48_/_0.6)]">{sub}</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * Publish this app's MEASURED chrome height as `--chrome-height`, for the upload
 * overlay's centring box (`top-header`, see tailwind.config.ts).
 *
 * WHY MEASURED AND NOT A CONSTANT. The Design has exactly one 64px header, so its
 * overlay offsets by `--header-height` and is correct. This app's chrome is taller and
 * width-dependent: `.tb-header` declares no height and carries `flex-wrap: wrap`, so it
 * grows as the viewport narrows and as the signed-in nav's content changes. Any hardcoded
 * number is wrong at some width. Offsetting by the Design's 64 put the modal 72.8px too
 * high and left `max-h-full` clamping against a box 73px taller than the space below the
 * chrome. Measuring is also why removing the synthetic-data banner needed no change here —
 * one bar fewer simply reads as a smaller offsetTop.
 *
 * `<main>` opens immediately after the last bar, so its offsetTop IS the chrome
 * height, wrapping included — one read, no per-bar arithmetic to drift.
 */
function useChromeHeightVar(mainRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const publish = () => {
      document.documentElement.style.setProperty('--chrome-height', `${main.offsetTop}px`);
    };
    publish();
    // The bars re-wrap on resize, so observe rather than measure once.
    const ro = new ResizeObserver(publish);
    ro.observe(document.documentElement);
    ro.observe(main);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--chrome-height');
    };
  }, [mainRef]);
}

/**
 * Record that the tour was finished or dismissed (PUT /api/me/tour). Fire-and-forget by design: the
 * overlay is already gone by the time this runs, and the caller does not wait on it.
 *
 * `keepalive` so a click on "Explore the platform" immediately followed by a navigation still lands.
 * A failure is swallowed on purpose — the person's flag stays null and the tour returns once on a
 * later sign-in. That is the right trade: a duplicate tour is a small annoyance, a tour that cannot be
 * dismissed is a broken product.
 */
export function App() {
  const { auth, reload } = useAuth();
  // (Staging computed a top-level `roleGatedEntries` here to pass into its ProfileMenu; this
  // branch's ProfileMenu takes `roles` and derives the dashboard gate itself via
  // roleGatedEntriesFor(), so that const is dropped in the merge to avoid an unused local.)
  // The Terms-of-Use gate (TU-F02..F06): a signed-in person who has not accepted the CURRENT policy
  // sees the gate in place of the app until they accept. It is deliberately INSIDE the frame (header,
  // nav and footer stay), so the person still knows where they are and the footer policy link works —
  // the gate replaces only the routed page, not the whole product.
  const mustAcceptTerms = auth.status === 'signed-in' && !auth.termsOfUse.acceptedCurrent;
  const mainRef = useRef<HTMLElement>(null);
  useChromeHeightVar(mainRef);
  const location = useLocation();

  const [tourParams, setTourParams] = useSearchParams();
  // The guided tour is intentionally retired. Strip obsolete replay URLs without opening a dialog.
  useEffect(() => {
    if (tourParams.get('tour') !== '1') return;
    const next = new URLSearchParams(tourParams);
    next.delete('tour');
    setTourParams(next, { replace: true });
  }, [tourParams, setTourParams]);

  return (
    // MotionConfig is LAYER 2 of the three-layer reduced-motion system that
    // design-system.css already documents (layer 1 is its
    // @media (prefers-reduced-motion: reduce) block, layer 3 is
    // usePrefersReducedMotion()). It was described there but never actually
    // mounted; this is it. Ported from the Design's App.tsx, same position:
    // outermost, so EVERY motion component in the tree inherits it and no
    // individual animation has to remember to check the preference.
    <MotionConfig reducedMotion="user">
      <div className="tb-frame">
        {/* No <style> tag here any more — the frame's CSS is `src/frame.css`,
            imported from main.tsx. Injecting it from inside this component meant
            the chrome had no layout until React mounted, which is what made a
            cold load flash a page of unstyled full-bleed blocks before snapping
            into the frame. See frame.css's header. */}

        {/* The tour's styles stay inline, unlike the frame's: every other overlay here (TermsGate,
            the upload overlay, LearningHub, Profile) inlines its own from a TS template literal, and
            the reason frame.css was extracted — a cold load painting unstyled blocks before React
            mounted — cannot apply to markup that does not exist until `tourOpen` is true. */}

        {/* PC-F23 IS NOT DROPPED HERE — the standing amber `.tb-banner` that staging renders in this
            slot is deliberately gone on this branch (see this file's header): it was removed at the
            user's request, its `.tb-banner` rule was deleted from frame.css with it, and the synthetic
            -data claim moved to the footer line ("Synthetic demo data throughout"), which is on every
            page for exactly the reason the banner was. Reinstating the markup here would paint an
            unstyled full-bleed div, since only the `--tb-banner-*` tokens survive — the six other
            files that use that amber as a generic note palette are why the tokens stayed. */}

        {/* THE MENU BAR (design system) — replaces the old `.tb-header` + `.tb-nav` chrome. It is
            the designer's "Menu Bar" frame (node 7:301), assembled in MenuBar.tsx from the design
            system's own primitives. The behaviour that lived in the old header stays HERE and is
            passed in as slots: GlobalSearch (wrapped in ResponsiveSearch so it collapses to an icon
            at narrow widths, the frame's "Search=Icon" state), the NotificationBell, and the
            account slot. See MenuBar.tsx's header for the two approved requirement deviations
            (PC-F10 nav trim, PC-F06a search collapse).

            The old primary <nav> (PRIMARY_NAV + role-gated entries) is intentionally NOT rendered
            as a bar: those destinations now live in the profile dropdown and on the Home page, per
            the redesign. The role gate moves into ProfileMenu, which takes `roles` and derives the
            Management dashboard row itself via roleGatedEntriesFor(). */}
        <MenuBar
          search={
            <ResponsiveSearch>
              <GlobalSearch />
            </ResponsiveSearch>
          }
          bell={<NotificationBell signedIn={auth.status === 'signed-in'} />}
          profile={
            auth.status === 'signed-in' ? (
              <ProfileMenu displayName={auth.displayName} roles={auth.roles} />
            ) : auth.status === 'signed-out' ? (
              // Signed out: the account slot becomes a plain Sign in link (full-page navigation to
              // the backend login route, which drives the Microsoft round-trip). The old
              // AuthControl's signed-in branch is now the ProfileMenu above; its signed-out branch
              // is this link.
              <a
                href="/auth/login?returnTo=/me"
                className="whitespace-nowrap rounded-md px-5 py-2 text-sm font-light text-ink transition-colors hover:bg-accent-subtle"
              >
                Sign in
              </a>
            ) : (
              // 'loading' / unknown: reserve the slot, assert nothing (matches the old AuthControl).
              <span aria-hidden="true" className="block size-[32px]" />
            )
          }
        />

        {/* The skip-link in index.html targets #main; this is that target. */}
        <main id="main" ref={mainRef} className="tb-main">
          {/* THE PAGE TRANSITION — the Design's own, from tools/upload/design/motion.ts
              (`pageTransition`, ported in an earlier pass but until now unused).
              `mode="wait"` makes it sequential: the outgoing page finishes its exit
              before the incoming one starts, which is what gives the Design its
              settled cross-fade rather than two pages dissolving through each other.
              `initial={false}` suppresses the animation on first mount, so a cold
              load or a hard refresh paints immediately instead of fading in.

              INSIDE <main>, NOT AROUND IT. useChromeHeightVar reads main.offsetTop to
              publish --chrome-height (the overlay's centring offset). Wrapping <main>
              would move mainRef onto an animated element whose transform/opacity
              churn feeds the ResizeObserver; keeping the wrapper inside leaves both
              the ref and the measurement exactly where they were.

              KEYED ON THE RAW PATHNAME, unlike the Design, which collapses /upload/*
              and /profile-full to one 'profile' key so intra-wizard steps don't
              re-animate the whole page. Here the upload overlay is component state on
              /share, not a route: opening a step or clicking off never changes the
              URL, so there is nothing to collapse. The wizard's own step-to-step
              AnimatePresence (UploadWizard) is unaffected and already matches the
              Design byte-for-byte. */}
          {mustAcceptTerms ? (
            // The gate stands in for the routed page. No AnimatePresence/key here: the gate is not a
            // route, and it must not be swept away by a page transition while it is the only thing the
            // person can act on. It re-probes auth on accept (reload), which flips mustAcceptTerms.
            <TermsGate onAccepted={reload} />
          ) : (
            // CROSS-FADE, NOT `mode="wait"`. The sequential form ran the outgoing page's 300ms
            // `exit` to completion BEFORE mounting the incoming one, so for those 300ms nothing
            // was painted and the reader saw the bare page white. On most routes that read as a
            // soft dip; going Home -> /me it was a hard white flash, because /me's hero is a
            // full-bleed navy band (.pp-band-bleed, --ink) — the eye crossed white-to-navy with
            // a full white frame in between.
            //
            // Dropping `mode` lets both pages be mounted at once: the incoming one fades UP while
            // the outgoing one fades DOWN, so at least one page is painted on every frame and the
            // blank window disappears.
            //
            // While both are mounted AnimatePresence renders the outgoing page FIRST and the
            // incoming one last, so `.tb-page:not(:last-child)` is exactly "the page on its way
            // out" — frame.css takes that one out of flow (position:absolute) so the two do not
            // stack vertically and shove the footer down for the length of the fade. No JS
            // bookkeeping needed. `initial={false}` still suppresses the animation on first mount,
            // so a cold load or hard refresh paints immediately instead of fading in.
            <div className="tb-page-stack">
              <AnimatePresence initial={false}>
                <motion.div
                  key={location.pathname}
                  className="tb-page"
                  variants={pageTransition}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </main>

        <footer className="tb-footer">
          <p>Trailblazer — internal tool catalogue. Synthetic demo data throughout.</p>
          {/* TU-F08: the Terms of Use are reachable from every page's footer, at all times — before
              and after acceptance. Opens the live policy in a dialog (same content as the gate). */}
          <TermsFooterLink />
        </footer>

        {auth.status === 'signed-in' && !mustAcceptTerms && (
          <FloatingShareAction routes={kombiProductionRoutes} />
        )}

        {/*
          Last child of the frame, after the footer: the overlay is `position: fixed` and renders in
          place (there are no portals anywhere in this app, and index.html has no portal target), so
          being last in DOM order keeps it clear of any stacking context page content creates. Its
          z-index band (60/61) sits above every modal and below the skip-link — see tour/styles.ts.

          `!mustAcceptTerms` is belt-and-braces alongside the open-effect's guard: if a new policy is
          published mid-session, the next reload() raises the gate and this unmounts the tour rather
          than leaving it stranded on top of it.
        */}
      </div>
    </MotionConfig>
  );
}
